/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports a pure function, not a React component. */
import type { DimeTreeComboboxRow } from './tree-combobox-model.js';

export type TreeComboboxKeyAction = {
  type: 'move' | 'expand' | 'collapse' | 'toggle';
  id: string;
};

type KeyContext = {
  rows: readonly DimeTreeComboboxRow[];
  activeIndex: number;
  active: DimeTreeComboboxRow;
};

function moveTo(row: DimeTreeComboboxRow | undefined): TreeComboboxKeyAction | null {
  return row ? { type: 'move', id: row.node.id } : null;
}

function arrowRight({ rows, activeIndex, active }: KeyContext) {
  if (active.isExpanded) return moveTo(rows[activeIndex + 1]);
  if (active.canExpand) return { type: 'expand', id: active.node.id } as const;
  return null;
}

function arrowLeft({ rows, active }: KeyContext) {
  if (active.isExpanded) return { type: 'collapse', id: active.node.id } as const;
  return moveTo(rows.find((candidate) => candidate.node.id === active.parentId));
}

/**
 * Maps a key pressed in the search input to a tree action (CT-6); once any text is typed,
 * ArrowLeft/ArrowRight belong to the input's text cursor, so they map to nothing.
 */
export function treeComboboxKeyAction(options: {
  key: string;
  rows: readonly DimeTreeComboboxRow[];
  activeIndex: number;
  /** True when the raw query is non-empty, including a whitespace-only query. */
  hasQueryText: boolean;
}): TreeComboboxKeyAction | null {
  const { key, rows, activeIndex, hasQueryText } = options;
  const active = rows[activeIndex];
  if (!active) return null;
  const context: KeyContext = { rows, activeIndex, active };

  switch (key) {
    case 'ArrowDown':
      return moveTo(rows[Math.min(activeIndex + 1, rows.length - 1)]);
    case 'ArrowUp':
      return moveTo(rows[Math.max(activeIndex - 1, 0)]);
    case 'ArrowRight':
      return hasQueryText ? null : arrowRight(context);
    case 'ArrowLeft':
      return hasQueryText ? null : arrowLeft(context);
    case 'Enter':
      return { type: 'toggle', id: active.node.id };
    default:
      return null;
  }
}
